import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Clock, 
  AlertTriangle, 
  AlertOctagon,
  RefreshCw, 
  Download, 
  Search,
  Filter,
  Package,
  IndianRupee,
  CalendarX,
  TrendingDown
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MaterialAgingPage() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [daysThreshold, setDaysThreshold] = useState(90);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/material-aging`, {
        params: { days_threshold: daysThreshold },
        headers: getAuthHeader()
      });
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load aging report');
      console.error('Report fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [daysThreshold]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  // Get unique categories
  const categories = [...new Set(reportData?.items?.map(item => item.category).filter(Boolean))];

  // Filter items
  const filteredItems = reportData?.items?.filter(item => {
    const matchesSearch = 
      item.item_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    const matchesSeverity = severityFilter === 'all' || item.severity === severityFilter;
    return matchesSearch && matchesCategory && matchesSeverity;
  }) || [];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertOctagon className="w-4 h-4" />;
      case 'high': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const exportToCSV = () => {
    if (!filteredItems.length) return;
    
    const headers = [
      'Item Code', 'Item Name', 'Category', 'Unit', 'Stock Qty', 'Avg Price',
      'Stock Value', 'Last Activity', 'Days Since Movement', 'Aging Bucket', 'Severity'
    ];
    
    const rows = filteredItems.map(item => [
      item.item_code,
      item.item_name,
      item.category,
      item.unit,
      item.stock_qty,
      item.avg_price,
      item.stock_value,
      item.last_activity_date ? format(new Date(item.last_activity_date), 'dd/MM/yyyy') : '-',
      item.days_since_movement,
      item.aging_bucket,
      item.severity
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `material_aging_report_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CalendarX className="w-6 h-6 text-orange-600" />
              Material Aging Report
            </h1>
            <p className="text-muted-foreground mt-1">
              Identify slow-moving and obsolete inventory
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportToCSV} disabled={!filteredItems.length}>
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {reportData?.summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Critical (&gt;1 Year)</p>
                    <p className="text-2xl font-bold text-red-700">{reportData.summary.critical_count}</p>
                    <p className="text-sm text-red-600">{formatCurrency(reportData.summary.critical_value)}</p>
                  </div>
                  <AlertOctagon className="w-10 h-10 text-red-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">High (6-12 Months)</p>
                    <p className="text-2xl font-bold text-orange-700">{reportData.summary.high_count}</p>
                    <p className="text-sm text-orange-600">{formatCurrency(reportData.summary.high_value)}</p>
                  </div>
                  <AlertTriangle className="w-10 h-10 text-orange-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-yellow-200 bg-yellow-50/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-700 font-medium">Medium (3-6 Months)</p>
                    <p className="text-2xl font-bold text-yellow-800">{reportData.summary.medium_count}</p>
                    <p className="text-sm text-yellow-700">{formatCurrency(reportData.summary.medium_value)}</p>
                  </div>
                  <Clock className="w-10 h-10 text-yellow-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-primary font-medium">Total Aging Value</p>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(reportData.summary.total_value)}</p>
                    <p className="text-sm text-muted-foreground">{reportData.summary.total_items} items</p>
                  </div>
                  <IndianRupee className="w-10 h-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search by item code or name..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Select value={daysThreshold.toString()} onValueChange={(v) => setDaysThreshold(parseInt(v))}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Threshold" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 Days</SelectItem>
                <SelectItem value="60">60 Days</SelectItem>
                <SelectItem value="90">90 Days</SelectItem>
                <SelectItem value="180">180 Days</SelectItem>
                <SelectItem value="365">365 Days</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-orange-600" />
              Slow-Moving Items
            </CardTitle>
            <CardDescription>
              Items with no movement for {daysThreshold}+ days ({filteredItems.length} items)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Item Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead className="text-center">Days Idle</TableHead>
                    <TableHead className="text-center">Severity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <div className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          Loading aging data...
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <Package className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
                        <p className="text-muted-foreground font-medium">No slow-moving items found</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          All items have been moved within the last {daysThreshold} days
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((item) => (
                      <TableRow key={item.item_id} className="hover:bg-muted/30">
                        <TableCell className="font-medium">{item.item_code}</TableCell>
                        <TableCell className="max-w-[150px] truncate" title={item.item_name}>
                          {item.item_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {item.category || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(item.stock_qty)} {item.unit}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.stock_value)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.last_activity_date 
                            ? format(new Date(item.last_activity_date), 'dd MMM yyyy')
                            : 'Never'
                          }
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-bold ${
                            item.days_since_movement >= 365 ? 'text-red-600' :
                            item.days_since_movement >= 180 ? 'text-orange-600' :
                            'text-yellow-600'
                          }`}>
                            {item.days_since_movement}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={`${getSeverityColor(item.severity)} flex items-center gap-1 justify-center`}>
                            {getSeverityIcon(item.severity)}
                            {item.aging_bucket}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
